import React, { useEffect, useState, useCallback } from "react";
import { useBuilding } from "../../context/BuildingContext";
import { useToast } from "../../components/Toast";
import { HABI_API_BASE_URL, getAuthHeaders } from "../../utils/habiApi";
import ScreenHeader from "../../components/ScreenHeader";
import { db } from "../../firebase";
import {
  collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc, Timestamp,
} from "firebase/firestore";
import { FaPlus, FaTrash, FaPenSquare } from "react-icons/fa";
import {
  colors,
  typography,
  cardBase,
  pageContainer,
  btnPrimary,
  btnSecondary,
} from "../../utils/designTokens";

/**
 * D3 — Mantenimiento preventivo predictivo.
 * - Si el edificio no tiene schedule, ofrece inicializarlo (con flags
 *   has_elevator / has_gas_central).
 * - Si tiene, muestra items: tipo, frecuencia, last_done, next_due,
 *   items que vencen en 30 dias destacados.
 * - Boton "Marcar como hecho" por item.
 */
export default function Mantenimiento({ setSelected }) {
  const { selectedBuilding } = useBuilding();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [needsInit, setNeedsInit] = useState(false);
  const [hasElevator, setHasElevator] = useState(false);
  const [hasGasCentral, setHasGasCentral] = useState(false);
  const [markingItem, setMarkingItem] = useState(null);

  // Historial de inspecciones (Sprint: unificación con la vieja pantalla
  // Revisiones — misma colección Firestore `inspections`).
  const [inspections, setInspections] = useState([]);
  const [loadingInsp, setLoadingInsp] = useState(false);
  const [showInspModal, setShowInspModal] = useState(false);
  const [editingInsp, setEditingInsp] = useState(null);
  const [inspForm, setInspForm] = useState({ title: "", company: "", date: "", status: "Completada" });
  const [savingInsp, setSavingInsp] = useState(false);

  const loadInspections = useCallback(async () => {
    if (!selectedBuilding?.buildingid) return;
    try {
      setLoadingInsp(true);
      const q = query(
        collection(db, "inspections"),
        where("buildingid", "==", Number(selectedBuilding.buildingid))
      );
      const snap = await getDocs(q);
      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      all.sort((a, b) => (b.date?.seconds || 0) - (a.date?.seconds || 0));
      setInspections(all);
    } catch (e) {
      console.error("[Mantenimientos] Error cargando inspecciones:", e);
    } finally {
      setLoadingInsp(false);
    }
  }, [selectedBuilding]);

  useEffect(() => {
    if (selectedBuilding) {
      loadSchedule();
      loadInspections();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBuilding]);

  const openInspCreate = () => {
    setEditingInsp(null);
    setInspForm({
      title: "",
      company: "",
      date: new Date().toISOString().slice(0, 10),
      status: "Completada",
    });
    setShowInspModal(true);
  };

  const openInspEdit = (item) => {
    setEditingInsp(item);
    const d = item.date?.seconds
      ? new Date(item.date.seconds * 1000).toISOString().slice(0, 10)
      : "";
    setInspForm({
      title: item.title || "",
      company: item.company || "",
      date: d,
      status: item.status || "Completada",
    });
    setShowInspModal(true);
  };

  const closeInspModal = () => {
    if (savingInsp) return;
    setShowInspModal(false);
    setEditingInsp(null);
  };

  const handleSaveInsp = async () => {
    if (!selectedBuilding) { showToast("Seleccioná un edificio primero", "warning"); return; }
    if (!inspForm.title.trim()) { showToast("El título es obligatorio", "error"); return; }
    if (!inspForm.date) { showToast("La fecha es obligatoria", "error"); return; }
    setSavingInsp(true);
    try {
      const payload = {
        buildingid: Number(selectedBuilding.buildingid),
        title: inspForm.title.trim(),
        company: inspForm.company.trim(),
        date: Timestamp.fromDate(new Date(inspForm.date + "T12:00:00")),
        status: inspForm.status,
        source: editingInsp?.source || "manual",
      };
      if (editingInsp) {
        await updateDoc(doc(db, "inspections", editingInsp.id), payload);
      } else {
        await addDoc(collection(db, "inspections"), payload);
      }
      showToast(editingInsp ? "Inspección actualizada." : "Inspección registrada.", "success");
      closeInspModal();
      loadInspections();
    } catch (e) {
      showToast("Error al guardar: " + e.message, "error");
    } finally {
      setSavingInsp(false);
    }
  };

  const handleDeleteInsp = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("¿Eliminar esta inspección del historial?")) return;
    try {
      await deleteDoc(doc(db, "inspections", id));
      loadInspections();
    } catch (e) {
      showToast("Error al eliminar: " + e.message, "error");
    }
  };

  const loadSchedule = async () => {
    if (!selectedBuilding) return;
    try {
      setLoading(true);
      const headers = await getAuthHeaders();
      const resp = await fetch(
        `${HABI_API_BASE_URL}/api/maintenance-schedule/${selectedBuilding.buildingid}`,
        { headers }
      );
      if (!resp.ok) return;
      const data = await resp.json();
      setSchedule(data);
      setNeedsInit(!!data.needs_init);
    } catch (e) {
      console.error("[D3] load error:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleInit = async () => {
    try {
      setLoading(true);
      const headers = await getAuthHeaders();
      const resp = await fetch(`${HABI_API_BASE_URL}/api/maintenance-schedule/init`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          building_id: String(selectedBuilding.buildingid),
          has_elevator: hasElevator,
          has_gas_central: hasGasCentral,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        showToast(err.detail || "No se pudo inicializar el schedule.", "error");
        return;
      }
      const data = await resp.json();
      showToast(`Inicializado con ${data.items_count} items.`, "success");
      setNeedsInit(false);
      loadSchedule();
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleMarkDone = async (itemType) => {
    try {
      setMarkingItem(itemType);
      const headers = await getAuthHeaders();
      const resp = await fetch(`${HABI_API_BASE_URL}/api/maintenance-schedule/mark-done`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          building_id: String(selectedBuilding.buildingid),
          item_type: itemType,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        showToast(err.detail || "No se pudo marcar como hecho.", "error");
        return;
      }
      // Sincronizar con el historial unificado: crear un doc en `inspections`
      // así el admin ve el mantenimiento realizado en la misma timeline que
      // las inspecciones manuales. No es bloqueante si falla.
      try {
        const item = (schedule?.items || []).find((it) => it.type === itemType);
        if (item) {
          await addDoc(collection(db, "inspections"), {
            buildingid: Number(selectedBuilding.buildingid),
            title: item.label,
            company: "",
            date: Timestamp.fromDate(new Date()),
            status: "Completada",
            source: "preventivo",
            item_type: itemType,
          });
        }
      } catch (e) {
        console.warn("[Mantenimientos] No se pudo registrar en inspections:", e);
      }
      showToast("Marcado como hecho. Próximo vencimiento recalculado.", "success");
      loadSchedule();
      loadInspections();
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setMarkingItem(null);
    }
  };

  if (!selectedBuilding) {
    return (
      <div style={pageContainer}>
        <ScreenHeader size="compact" title="Mantenimientos" subtitle="Schedule de mantenimientos recurrentes" />
        <div style={{ ...cardBase, padding: 24, marginTop: 16 }}>
          <p style={{ color: colors.gray600 }}>Seleccioná un edificio.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={pageContainer}>
      <ScreenHeader
        size="compact"
        title="Mantenimientos"
        subtitle={`${selectedBuilding.name || `Edificio ${selectedBuilding.buildingid}`} · schedule de mantenimientos recurrentes`}
      />

      {loading && (
        <div style={{ marginTop: 16, color: colors.gray600 }}>Cargando…</div>
      )}

      {!loading && needsInit && (
        <div style={{ ...cardBase, padding: 20, marginTop: 16 }}>
          <h3 style={{ margin: 0, fontSize: typography.sizes.md, fontWeight: 400 }}>
            Aún no hay schedule de mantenimiento
          </h3>
          <p style={{ fontSize: 13, color: colors.gray600, margin: "8px 0 16px" }}>
            Vamos a crear uno con los items recomendados por normativa UY
            (matafuegos, tanque de agua, electricidad, etc.). Marcá si el
            edificio tiene ascensor o caldera central.
          </p>
          <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <input type="checkbox" checked={hasElevator} onChange={(e) => setHasElevator(e.target.checked)} />
            <span style={{ fontSize: 13 }}>Tiene ascensor (agrega revisión semestral + habilitación anual de intendencia)</span>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
            <input type="checkbox" checked={hasGasCentral} onChange={(e) => setHasGasCentral(e.target.checked)} />
            <span style={{ fontSize: 13 }}>Tiene caldera / gas central (agrega mantenimiento URSEA anual)</span>
          </label>
          <button type="button" onClick={handleInit} disabled={loading} style={btnPrimary}>
            Inicializar schedule
          </button>
        </div>
      )}

      {!loading && !needsInit && schedule?.items && (
        <>
          {schedule.items_due_soon && schedule.items_due_soon.length > 0 && (
            <div style={{
              ...cardBase,
              padding: 16,
              marginTop: 16,
              background: colors.warningLight,
            }}>
              <h3 style={{ margin: 0, fontSize: typography.sizes.md, fontWeight: 400, color: colors.warning }}>
                ⚠ Próximos a vencer ({schedule.items_due_soon.length})
              </h3>
              <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                {schedule.items_due_soon.map((it) => (
                  <DueItemRow
                    key={it.type}
                    item={it}
                    onMarkDone={() => handleMarkDone(it.type)}
                    marking={markingItem === it.type}
                  />
                ))}
              </div>
            </div>
          )}

          <div style={{ ...cardBase, padding: 16, marginTop: 16 }}>
            <h3 style={{ margin: 0, fontSize: typography.sizes.md, fontWeight: 400 }}>
              Todos los items del schedule
            </h3>
            <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
              {schedule.items.map((it) => (
                <ScheduleItemRow
                  key={it.type}
                  item={it}
                  onMarkDone={() => handleMarkDone(it.type)}
                  marking={markingItem === it.type}
                />
              ))}
            </div>
          </div>
        </>
      )}

      {/* Historial unificado de inspecciones (incluye las que vienen del
          schedule preventivo y las cargadas manualmente). */}
      {!loading && !needsInit && (
        <div style={{ ...cardBase, padding: 16, marginTop: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
            <h3 style={{ margin: 0, fontSize: typography.sizes.md, fontWeight: 400 }}>
              Historial de inspecciones {inspections.length > 0 && <span style={{ color: colors.gray600 }}>({inspections.length})</span>}
            </h3>
            <button
              type="button"
              onClick={openInspCreate}
              style={{ ...btnSecondary, padding: "6px 12px", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <FaPlus size={11} /> Cargar inspección
            </button>
          </div>

          {loadingInsp && (
            <div style={{ marginTop: 12, color: colors.gray600, fontSize: 13 }}>Cargando…</div>
          )}

          {!loadingInsp && inspections.length === 0 && (
            <div style={{ marginTop: 12, padding: 14, background: colors.gray50, borderRadius: 8, color: colors.gray600, fontSize: 13, textAlign: "center" }}>
              Todavía no hay inspecciones registradas. Marcá un item del schedule como hecho o cargá una inspección manual.
            </div>
          )}

          {!loadingInsp && inspections.length > 0 && (
            <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
              {inspections.map((insp) => (
                <InspectionRow
                  key={insp.id}
                  item={insp}
                  onEdit={() => openInspEdit(insp)}
                  onDelete={() => handleDeleteInsp(insp.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal: alta/edición de inspección manual */}
      {showInspModal && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
            zIndex: 1000, display: "flex", alignItems: "center",
            justifyContent: "center", padding: 20,
          }}
          onClick={closeInspModal}
        >
          <div
            style={{
              background: colors.white, borderRadius: 12, maxWidth: 520, width: "100%",
              padding: 20, boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: 0, fontSize: typography.sizes.lg, fontWeight: 400, color: colors.gray900 }}>
              {editingInsp ? "Editar inspección" : "Cargar inspección"}
            </h3>
            <p style={{ fontSize: 12, color: colors.gray600, margin: "4px 0 16px" }}>
              Cargá una inspección puntual fuera del schedule recurrente.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <label style={{ fontSize: 12, color: colors.gray700 }}>
                Título *
                <input
                  type="text"
                  value={inspForm.title}
                  onChange={(e) => setInspForm({ ...inspForm, title: e.target.value })}
                  style={{ width: "100%", padding: 8, fontSize: 13, marginTop: 4, border: `1px solid ${colors.gray300}`, borderRadius: 6, boxSizing: "border-box" }}
                  placeholder="Ej: Revisión semestral ascensor"
                />
              </label>
              <label style={{ fontSize: 12, color: colors.gray700 }}>
                Empresa
                <input
                  type="text"
                  value={inspForm.company}
                  onChange={(e) => setInspForm({ ...inspForm, company: e.target.value })}
                  style={{ width: "100%", padding: 8, fontSize: 13, marginTop: 4, border: `1px solid ${colors.gray300}`, borderRadius: 6, boxSizing: "border-box" }}
                />
              </label>
              <label style={{ fontSize: 12, color: colors.gray700 }}>
                Fecha *
                <input
                  type="date"
                  value={inspForm.date}
                  onChange={(e) => setInspForm({ ...inspForm, date: e.target.value })}
                  style={{ width: "100%", padding: 8, fontSize: 13, marginTop: 4, border: `1px solid ${colors.gray300}`, borderRadius: 6, boxSizing: "border-box" }}
                />
              </label>
              <label style={{ fontSize: 12, color: colors.gray700 }}>
                Estado
                <select
                  value={inspForm.status}
                  onChange={(e) => setInspForm({ ...inspForm, status: e.target.value })}
                  style={{ width: "100%", padding: 8, fontSize: 13, marginTop: 4, border: `1px solid ${colors.gray300}`, borderRadius: 6, boxSizing: "border-box" }}
                >
                  <option value="Programada">Programada</option>
                  <option value="Completada">Completada</option>
                </select>
              </label>
            </div>
            <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button type="button" onClick={closeInspModal} disabled={savingInsp} style={{ ...btnSecondary, padding: "6px 14px", fontSize: 13 }}>
                Cancelar
              </button>
              <button type="button" onClick={handleSaveInsp} disabled={savingInsp} style={{ ...btnPrimary, padding: "6px 14px", fontSize: 13 }}>
                {savingInsp ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InspectionRow({ item, onEdit, onDelete }) {
  const dateStr = item.date?.seconds
    ? new Date(item.date.seconds * 1000).toLocaleDateString("es-UY", { day: "2-digit", month: "2-digit", year: "numeric" })
    : "—";
  const isPreventivo = item.source === "preventivo";
  return (
    <div style={{
      padding: 10,
      background: colors.gray50,
      borderRadius: 8,
      display: "grid",
      gridTemplateColumns: "1fr auto",
      gap: 12,
      alignItems: "center",
    }}>
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <strong style={{ fontWeight: 400, fontSize: 13, color: colors.gray900 }}>{item.title}</strong>
          {isPreventivo && (
            <span style={{ padding: "1px 8px", fontSize: 10, borderRadius: 9999, background: colors.primaryLighter, color: colors.primary, textTransform: "uppercase", letterSpacing: 0.4 }}>
              Schedule
            </span>
          )}
          <span style={{ padding: "1px 8px", fontSize: 10, borderRadius: 9999, background: item.status === "Completada" ? colors.successLight : colors.warningLight, color: item.status === "Completada" ? colors.successDark : colors.warningDark }}>
            {item.status || "—"}
          </span>
        </div>
        <div style={{ fontSize: 12, color: colors.gray700, marginTop: 2 }}>
          {dateStr}
          {item.company && <span> · {item.company}</span>}
        </div>
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <button
          type="button"
          onClick={onEdit}
          title="Editar"
          style={{ padding: "4px 8px", fontSize: 11, color: colors.gray700, background: "transparent", border: `1px solid ${colors.gray300}`, borderRadius: 6, cursor: "pointer" }}
        >
          <FaPenSquare size={11} />
        </button>
        <button
          type="button"
          onClick={onDelete}
          title="Eliminar"
          style={{ padding: "4px 8px", fontSize: 11, color: colors.error, background: "transparent", border: `1px solid ${colors.errorLight}`, borderRadius: 6, cursor: "pointer" }}
        >
          <FaTrash size={11} />
        </button>
      </div>
    </div>
  );
}

function DueItemRow({ item, onMarkDone, marking }) {
  const isOverdue = item.days_to_due < 0;
  return (
    <div style={{
      padding: 10,
      background: colors.white,
      borderRadius: 8,
      display: "grid",
      gridTemplateColumns: "1fr auto",
      gap: 12,
      alignItems: "center",
    }}>
      <div>
        <strong style={{ fontWeight: 400, fontSize: 13 }}>{item.label}</strong>
        <div style={{ fontSize: 12, color: isOverdue ? colors.error : colors.gray700, marginTop: 2 }}>
          {isOverdue
            ? `Vencido hace ${-item.days_to_due} días`
            : `Vence en ${item.days_to_due} días (${item.next_due_date})`}
        </div>
        {item.legal_basis && (
          <div style={{ fontSize: 11, color: colors.gray600, marginTop: 2, fontStyle: "italic" }}>
            {item.legal_basis}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onMarkDone}
        disabled={marking}
        style={{ ...btnSecondary, padding: "6px 14px", fontSize: 12 }}
      >
        {marking ? "…" : "Marcar hecho"}
      </button>
    </div>
  );
}

function ScheduleItemRow({ item, onMarkDone, marking }) {
  return (
    <div style={{
      padding: 10,
      background: colors.gray50,
      borderRadius: 8,
      display: "grid",
      gridTemplateColumns: "1fr auto",
      gap: 12,
      alignItems: "center",
    }}>
      <div>
        <strong style={{ fontWeight: 400, fontSize: 13 }}>{item.label}</strong>
        <div style={{ fontSize: 12, color: colors.gray700, marginTop: 2 }}>
          Frecuencia: cada {item.frequency_months} meses
          {item.last_done_date && <span> · Última: {item.last_done_date}</span>}
          {item.next_due_date && <span> · Próximo: {item.next_due_date}</span>}
        </div>
      </div>
      <button
        type="button"
        onClick={onMarkDone}
        disabled={marking}
        style={{ ...btnSecondary, padding: "4px 12px", fontSize: 11 }}
      >
        {marking ? "…" : "Marcar hecho"}
      </button>
    </div>
  );
}
